/**
 * ImageDetailsPopover — bubble-menu control exposing an image's metadata.
 *
 * Alt text stays semantic metadata (never rendered into the article body);
 * the caption is visible copy rendered beneath the image by the ImageExtension
 * NodeView. Helper copy reflects the publish policy implemented in
 * rteConverter's renderImageCaptionHTML: AI image captions publish, stock
 * images get a derived photo credit, generic non-AI captions are editor-only.
 */
import React, { useEffect, useId, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Captions } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface ImageDetailsPopoverProps {
  editor: Editor;
}

function readImageStrings(editor: Editor): { alt: string; caption: string } {
  const attrs = editor.getAttributes('image');
  return {
    alt: typeof attrs.alt === 'string' ? attrs.alt : '',
    caption: typeof attrs.caption === 'string' ? attrs.caption : '',
  };
}

interface ImageDetailsSummary {
  hasAlt: boolean;
  isAIGenerated: boolean;
  hasStockAttribution: boolean;
}

function readImageSummary(editor: Editor): ImageDetailsSummary {
  const attrs = editor.getAttributes('image');
  return {
    hasAlt: typeof attrs.alt === 'string' && attrs.alt.trim().length > 0,
    isAIGenerated: Boolean(attrs.isAIGenerated),
    hasStockAttribution: Boolean(
      attrs.isStockPhoto || attrs.attributionText || attrs.photographerName
    ),
  };
}

export function ImageDetailsPopover({ editor }: ImageDetailsPopoverProps) {
  const fieldId = useId();
  const [open, setOpen] = useState(false);
  const [draftAlt, setDraftAlt] = useState('');
  const [draftCaption, setDraftCaption] = useState('');

  // Track the selected image's summary via a plain transaction listener with
  // referentially-stable state: setState bails out (same reference) when
  // nothing changed, so transaction bursts cannot cause re-render loops.
  const [details, setDetails] = useState<ImageDetailsSummary>(() => readImageSummary(editor));
  useEffect(() => {
    const update = () => {
      const next = readImageSummary(editor);
      setDetails((prev) =>
        prev.hasAlt === next.hasAlt &&
        prev.isAIGenerated === next.isAIGenerated &&
        prev.hasStockAttribution === next.hasStockAttribution
          ? prev
          : next
      );
    };
    update();
    editor.on('transaction', update);
    return () => {
      editor.off('transaction', update);
    };
  }, [editor]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // Snapshot the selected image's current values on open so the fields are
      // never stale when the user switches between images.
      const { alt, caption } = readImageStrings(editor);
      setDraftAlt(alt);
      setDraftCaption(caption);
    }
    setOpen(nextOpen);
  };

  const saveDetails = () => {
    const alt = draftAlt.trim();
    const caption = draftCaption.trim();
    editor
      .chain()
      .focus()
      .updateAttributes('image', { alt, caption: caption || null })
      .run();
    setOpen(false);
  };

  const captionHelp = details.isAIGenerated
    ? 'Shown beneath the image and publishes with the article.'
    : details.hasStockAttribution
      ? 'Shown beneath the image in the editor. The published article shows an automatic photo credit instead.'
      : 'Shown beneath the image in the editor. Captions on this image will not publish with the article.';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Image details"
          title={
            details.hasAlt
              ? 'Image details (alt text & caption)'
              : 'Image details — alt text missing'
          }
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded transition-colors text-xs font-medium',
            details.hasAlt
              ? 'bg-teal-light text-teal-dark hover:bg-teal-light/80'
              : 'bg-amber-light text-amber hover:bg-amber-light/80'
          )}
        >
          <Captions className="w-3.5 h-3.5" />
          Details
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor={`${fieldId}-alt`} className="text-xs font-medium">
              Alt text
            </Label>
            <Input
              id={`${fieldId}-alt`}
              type="text"
              placeholder="Describe what's in the image..."
              value={draftAlt}
              onChange={(e) => setDraftAlt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveDetails();
                }
              }}
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Read by screen readers and search engines. Not displayed on the page.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${fieldId}-caption`} className="text-xs font-medium">
              Caption
            </Label>
            <Textarea
              id={`${fieldId}-caption`}
              rows={2}
              placeholder="Add a caption shown beneath the image..."
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveDetails();
                }
              }}
              className="text-sm resize-none"
            />
            <p className="text-xs text-muted-foreground">{captionHelp}</p>
          </div>
          <button
            type="button"
            onClick={saveDetails}
            className="w-full px-3 py-1.5 text-sm font-medium text-white bg-coral rounded hover:bg-coral-hover transition-colors"
          >
            Save details
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ImageDetailsPopover;
